/**
 * PhoneInput - Re-exports elSDK PhoneInput with dynamic country loading hook
 */

import { useState, useEffect } from 'react'

import {
  PhoneInput as ElPhoneInput,
  type PhoneInputProps as ElPhoneInputProps,
  type PhoneCountryOption,
} from '@elsapiens/ui'

import { getCountries } from '../../services/geolocation-service'

export type { PhoneValue, PhoneInputProps } from '@elsapiens/ui'

/**
 * Hook to load countries from the geolocation service and format them
 * as PhoneCountryOption[] for the elSDK PhoneInput.
 */
function usePhoneCountries(): { countries: PhoneCountryOption[]; isLoading: boolean } {
  const [countries, setCountries] = useState<PhoneCountryOption[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const loadCountries = async (): Promise<void> => {
      setIsLoading(true)
      try {
        const data = await getCountries()

        const options: PhoneCountryOption[] = data
          .filter(country => country.telephone)
          .map(country => ({
            value: country.identifier,
            label: `${country.name} (${country.telephone})`,
            description: country.name,
            data: {
              flag: country.image ?? country.identifier,
              /* v8 ignore next */
              telephone: country.telephone ?? '',
              countryCode: country.identifier,
            },
          }))
          .sort((a, b) => {
            /* v8 ignore next 2 */
            const aNum = parseInt((a.data?.telephone ?? '').replace('+', ''))
            const bNum = parseInt((b.data?.telephone ?? '').replace('+', ''))
            return aNum - bNum
          })

        setCountries(options)
      } catch {
        // silently ignore
      } finally {
        setIsLoading(false)
      }
    }

    void loadCountries()
  }, [])

  return { countries, isLoading }
}

/**
 * PhoneInput with dynamic country loading from geolocation service.
 * Wraps the elSDK PhoneInput and passes dynamically loaded countries.
 */
export function PhoneInput(props: Omit<ElPhoneInputProps, 'countries' | 'isLoading'>): JSX.Element {
  const { countries, isLoading } = usePhoneCountries()

  return (
    <ElPhoneInput
      {...props}
      {...(countries.length > 0 ? { countries } : {})}
      isLoading={isLoading}
    />
  )
}

export default PhoneInput
